import { datadogRum } from '@datadog/browser-rum';
import { logger } from 'features/logger/logger';

// Mind that Datadog custom performance metrics (vitals) names can't contain spaces
// or special characters so avoid it keep the same names for Performance API and Datadog
type PerformanceMeasures =
  | 'proseRenderWasmCall'
  | 'proseRenderResponseProcessing'
  | 'proseStartRenderWasmCall'
  | 'proseStartRenderResponseProcessing'
  | 'proseEditWithPreviewWasmCall'
  | 'proseEditWithPreviewResponseProcessing'
  | 'proseStartEditWithPreviewWasmCall'
  | 'proseStartEditWithPreviewResponseProcessing'
  | 'proseSubmissionRenderWasmCall'
  | 'proseSubmissionRenderResponseProcessing'
  | 'proseScheduleWasmCall'
  | 'questionnaireInitializeThunk'
  | 'questionnaireDocumentLoad'
  | 'questionnaireDocumentUpdate'
  | 'questionnaireCommitThunk'
  | 'questionnaireProseThunk'
  | 'signIn'
  | 'startFlow'
  | 'firstQuestion';

type PerformanceMeasureConfig = {
  detail?: object;
  console?: boolean;
  datadog?: boolean;
};

export const warnMeasureFailed = (
  name: PerformanceMeasures,
  error: unknown
): void => {
  logger.warn(
    'Performance measure failed',
    { details: { name } },
    error as Error
  );
};

export const hasPerformanceMark = (name: string): boolean =>
  performance.getEntriesByName(name, 'mark').length > 0;

/**
 * The start time of a vital measured from navigation start, as a performance
 * timestamp: 1 ms after the time origin rounded up to a whole Unix millisecond.
 */
const navigationVitalStartTime = (): number =>
  Math.ceil(performance.timeOrigin) + 1 - performance.timeOrigin;

// `visibility-state` entries and `activationStart` are missing in some
// browsers (Safari among them).
const visibilityChangeBeforeLoad = (): number => {
  try {
    const [navigation] = performance.getEntriesByType('navigation') as (
      (PerformanceEntry & { activationStart?: number }) | undefined
    )[];
    const changes = [
      navigation?.activationStart ?? 0,
      ...performance
        .getEntriesByType('visibility-state')
        .map(entry => entry.startTime),
    ].filter(time => time > 0);
    return Math.max(...changes);
  } catch {
    return -Infinity;
  }
};

// Performance-timeline time of the latest visibility change; a measure that
// overlaps a hidden period is not sent to Datadog.
let lastVisibilityChange = visibilityChangeBeforeLoad();
const recordVisibilityChange = () => {
  lastVisibilityChange = performance.now();
};
document.addEventListener('visibilitychange', recordVisibilityChange);
document.addEventListener('prerenderingchange', recordVisibilityChange);

const wasHiddenSince = (startTime: number) =>
  document.visibilityState !== 'visible' || lastVisibilityChange >= startTime;

export const addPerformanceStartMark = (
  name: PerformanceMeasures,
  startTime?: number
) => {
  try {
    performance.mark(
      `${name}Start`,
      startTime === undefined ? undefined : { startTime }
    );
  } catch (error) {
    warnMeasureFailed(name, error);
  }
};

export const addPerformanceEndMark = (
  name: PerformanceMeasures,
  config: PerformanceMeasureConfig = { console: false, datadog: false }
) => {
  try {
    const startMark = `${name}Start`;
    const endMark = `${name}End`;

    performance.mark(endMark);
    const measure = performance.measure(name, {
      detail: config.detail,
      start: startMark,
      end: endMark,
    });

    // TODO: Consider output config overrides through global object
    // TODO: Consider limiting sending data to Datadog (sampling subset of sessions)

    if (config.console) {
      console.debug(measure);
    }

    if (config.datadog && !wasHiddenSince(measure.startTime)) {
      const vitalOptions: {
        startTime: number;
        duration: number;
        context?: object;
      } = {
        // Datadog expects a timestamp as startTime but performance marks are created
        // with performance.now (which is the elapsed time from timeOrigin)
        startTime: performance.timeOrigin + measure.startTime,
        duration: measure.duration,
      };

      if (config.detail) {
        vitalOptions.context = config.detail;
      }

      datadogRum.addDurationVital(name, vitalOptions);
    }
  } catch (error) {
    warnMeasureFailed(name, error);
  }
};

const sentMark = (name: PerformanceMeasures) => `${name}Sent`;

export const wasNavigationVitalSent = (name: PerformanceMeasures): boolean =>
  hasPerformanceMark(sentMark(name));

/**
 * Sends the vital `name`, measured from navigation start to now, at most once
 * per page load and only when `canSend` returns true.
 */
export const sendNavigationVitalOnce = (
  name: PerformanceMeasures,
  canSend: () => boolean
): void => {
  try {
    if (wasNavigationVitalSent(name) || !canSend()) return;
    performance.mark(sentMark(name));
  } catch (error) {
    warnMeasureFailed(name, error);
    return;
  }
  addPerformanceStartMark(name, navigationVitalStartTime());
  addPerformanceEndMark(name, { datadog: true });
};
